/**
 * NeoCore · Movimientos de caja con filtros
 *
 * Reporte detallado de movimiento_caja con filtros:
 * - Rango de fechas
 * - Tipo de movimiento
 * - Asociado (search)
 * - Rango de monto
 * - Incluir anulados
 * - Search libre (descripción, correlativo)
 *
 * Task #142 · sesión Carlitos.
 */

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Funnel, X, Printer, FilePdf, MicrosoftExcelLogo } from '@phosphor-icons/react'
import { exportarPDF, exportarExcel } from '@/lib/exportReport'
import { fetchConfigReporte } from '@/lib/useConfigReporte'
import { haceDiasGT, hoyGT } from '../lib/fechas'

type Row = {
  id: string
  fecha: string
  hora: string | null
  correlativo: number
  tipo_codigo: string
  tipo_nombre: string
  asociado_id: string | null
  asociado_nombre: string | null
  no_asociado: string | null
  monto: number
  descripcion: string | null
  anulado: boolean
  anulado_at: string | null
  registrado_por: string
  poliza_id: string | null
  poliza_numero: number | null
  forma_pago: string | null
  detalle_pago: string | null
  referencia_banco: string | null
  total_count: number
}

type TipoOpt = { codigo: string; nombre: string }

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const hoy = () => hoyGT()
const hace30 = () => haceDiasGT(30)

export function NeoCoreMovimientosCajaPage() {
  const [tipos, setTipos] = useState<TipoOpt[]>([])
  const [rows, setRows] = useState<Row[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Filtros
  const [desde, setDesde] = useState(hace30())
  const [hasta, setHasta] = useState(hoy())
  const [tipo, setTipo] = useState('')
  const [search, setSearch] = useState('')
  const [montoMin, setMontoMin] = useState('')
  const [montoMax, setMontoMax] = useState('')
  const [incluirAnulados, setIncluirAnulados] = useState(false)
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 100

  useEffect(() => {
    supabase.from('tipo_movimiento_caja').select('codigo, nombre').order('codigo')
      .then(({ data }) => setTipos((data ?? []) as TipoOpt[]))
  }, [])

  const filtros = () => ({
    p_fecha_desde: desde || null,
    p_fecha_hasta: hasta || null,
    p_tipo_codigo: tipo || null,
    p_search: search || null,
    p_monto_min: montoMin ? Number(montoMin) : null,
    p_monto_max: montoMax ? Number(montoMax) : null,
    p_incluir_anulados: incluirAnulados,
  })

  const cargar = async () => {
    setLoading(true); setError(null)
    const { data, error } = await supabase.rpc('listar_movimientos_caja', {
      ...filtros(),
      p_limit: PAGE_SIZE,
      p_offset: page * PAGE_SIZE,
    })
    setLoading(false)
    if (error) { setError(error.message); return }
    const list = (data ?? []) as Row[]
    setRows(list)
    setTotal(list[0]?.total_count ?? 0)
  }

  useEffect(() => { cargar() }, [page])

  const totalMonto = rows.reduce((s, r) => s + (r.anulado ? 0 : Number(r.monto)), 0)
  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // Export = TODAS las páginas con los filtros actuales (no solo lo visible). Se pide en lotes de
  // 1000 para no chocar con el tope de filas de PostgREST.
  const traerTodo = async (): Promise<Row[]> => {
    const todos: Row[] = []
    for (let off = 0; ; off += 1000) {
      const { data, error } = await supabase.rpc('listar_movimientos_caja', { ...filtros(), p_limit: 1000, p_offset: off })
      if (error) throw new Error(error.message)
      const lote = (data ?? []) as Row[]
      todos.push(...lote)
      if (lote.length < 1000) break
    }
    return todos
  }

  const buildExport = async () => {
    const [config, todos] = await Promise.all([fetchConfigReporte(), traerTodo()])
    const headers = ['Fecha', 'Corr.', 'Tipo', 'Asociado', 'Monto', 'Forma pago', 'Detalle bancario', 'Ref. banco', 'Descripción', 'Póliza', 'Estado']
    const totalTodo = todos.reduce((s, r) => s + (r.anulado ? 0 : Number(r.monto)), 0)
    const filas: (string | number)[][] = todos.map(r => [
      r.fecha,
      r.correlativo,
      `${r.tipo_codigo} ${r.tipo_nombre}`,
      r.asociado_nombre ? `${r.asociado_nombre} (${r.no_asociado ?? ''})` : '—',
      `Q ${fmt(r.monto)}`,
      r.forma_pago ?? '',
      r.detalle_pago ?? '',
      r.referencia_banco ?? '',
      r.descripcion ?? '',
      r.poliza_numero ?? '',
      r.anulado ? 'Anulado' : 'OK',
    ])
    const totalRow = ['', '', '', `TOTAL (${todos.length} mov.)`, `Q ${fmt(totalTodo)}`, '', '', '', '', '', '']
    return { config, headers, filas, totalRow }
  }

  const onExportPDF = async () => {
    const { config, headers, filas, totalRow } = await buildExport()
    await exportarPDF({
      config,
      titulo: 'Movimientos de caja',
      subtitulo: `Del ${desde} al ${hasta}${tipo ? ` · tipo ${tipo}` : ''}`,
      headers, rows: filas, totalRow,
      orientacion: 'landscape',
      columnasNumericas: [4],
      filename: 'movimientos-caja',
    })
  }

  const onExportExcel = async () => {
    const { config, headers, filas, totalRow } = await buildExport()
    await exportarExcel({
      config,
      titulo: 'Movimientos de caja',
      subtitulo: `Del ${desde} al ${hasta}${tipo ? ` · tipo ${tipo}` : ''}`,
      headers, rows: filas, totalRow,
      filename: 'movimientos-caja',
    })
  }

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 500 }}>Movimientos de caja</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--muted-foreground)', fontSize: 13 }}>
          Filtro por fecha, tipo, asociado, monto. Total {total.toLocaleString()} movimientos que cumplen filtros.
        </p>
      </div>

      {/* Filtros */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 1fr 1fr auto auto auto auto',
        gap: 8, marginBottom: 12, padding: 12,
        background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8,
        alignItems: 'center',
      }}>
        <Funnel size={16} color="var(--muted-foreground)" />
        <input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); setPage(0) }}
          style={inputStyle} title="Desde" />
        <input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); setPage(0) }}
          style={inputStyle} title="Hasta" />
        <select value={tipo} onChange={(e) => { setTipo(e.target.value); setPage(0) }} style={inputStyle}>
          <option value="">Todos los tipos</option>
          {tipos.map((t) => (
            <option key={t.codigo} value={t.codigo}>{t.codigo} · {t.nombre}</option>
          ))}
        </select>
        <input placeholder="Search descripción, asociado, correlativo…"
          value={search} onChange={(e) => setSearch(e.target.value)} style={inputStyle} />
        <button onClick={() => { setPage(0); cargar() }} disabled={loading} style={btnPrimary}>
          {loading ? 'Cargando…' : 'Filtrar'}
        </button>
        <button onClick={onExportPDF} style={btnGhost} title="Exportar PDF">
          <FilePdf size={13} /> PDF
        </button>
        <button onClick={onExportExcel} style={btnGhost} title="Exportar Excel">
          <MicrosoftExcelLogo size={13} /> Excel
        </button>
        <button onClick={() => window.print()} style={btnGhost}>
          <Printer size={13} /> Imprimir
        </button>
      </div>

      {/* Segunda fila de filtros */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', fontSize: 12,
        padding: '4px 12px', color: 'var(--muted-foreground)',
      }}>
        <label>Monto:</label>
        <input type="number" placeholder="min" value={montoMin} onChange={(e) => setMontoMin(e.target.value)}
          style={{ ...inputStyle, width: 90 }} />
        <input type="number" placeholder="max" value={montoMax} onChange={(e) => setMontoMax(e.target.value)}
          style={{ ...inputStyle, width: 90 }} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 12 }}>
          <input type="checkbox" checked={incluirAnulados} onChange={(e) => setIncluirAnulados(e.target.checked)} />
          Incluir anulados
        </label>
        {(tipo || search || montoMin || montoMax || incluirAnulados) && (
          <button onClick={() => {
            setTipo(''); setSearch(''); setMontoMin(''); setMontoMax(''); setIncluirAnulados(false); setPage(0); cargar()
          }} style={{ ...btnGhost, padding: '4px 8px' }}>
            <X size={12} /> Limpiar
          </button>
        )}
        <div style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums', color: 'var(--ink)' }}>
          Total página: <b>Q {fmt(totalMonto)}</b>
        </div>
      </div>

      {error && (
        <div style={{ padding: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Tabla */}
      <div style={{ background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--n-50, #f4f4f5)', borderBottom: '1px solid var(--border)' }}>
            <tr>
              <th style={th}>Fecha</th>
              <th style={th}>Hora</th>
              <th style={th}>Corr.</th>
              <th style={th}>Tipo</th>
              <th style={th}>Asociado</th>
              <th style={{ ...th, textAlign: 'right' }}>Monto</th>
              <th style={th}>Forma pago</th>
              <th style={th}>Detalle bancario</th>
              <th style={th}>Descripción</th>
              <th style={th}>Póliza</th>
              <th style={th}>Registrado</th>
              <th style={th}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={12} style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)' }}>
                Sin movimientos con estos filtros
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} style={{
                borderBottom: '1px solid var(--border)',
                opacity: r.anulado ? 0.5 : 1,
                background: r.anulado ? '#fef3c7' : undefined,
              }}>
                <td style={td}>{r.fecha}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.hora?.slice(0, 5) ?? ''}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.correlativo}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.tipo_codigo}</td>
                <td style={td}>
                  {r.asociado_nombre ? (
                    <>
                      <div>{r.asociado_nombre}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted-foreground)' }}>{r.no_asociado}</div>
                    </>
                  ) : <span style={{ color: 'var(--muted-foreground)' }}>—</span>}
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                  Q {fmt(r.monto)}
                </td>
                <td style={{ ...td, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  {r.forma_pago ? (
                    <FormaPagoPill forma={r.forma_pago} />
                  ) : <span style={{ color: 'var(--muted-foreground)' }}>—</span>}
                </td>
                <td style={{ ...td, fontSize: 11, maxWidth: 260 }}>
                  {r.detalle_pago ? (
                    <div style={{ whiteSpace: 'pre-line' }}>{r.detalle_pago.replace(/ \| /g, '\n')}</div>
                  ) : !r.referencia_banco && <span style={{ color: 'var(--muted-foreground)' }}>—</span>}
                  {r.referencia_banco && (
                    <div style={{ fontFamily: 'monospace', color: '#C2410C', marginTop: 2 }}>ref: {r.referencia_banco}</div>
                  )}
                </td>
                <td style={{ ...td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.descripcion}
                </td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.poliza_numero ?? '—'}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.registrado_por}</td>
                <td style={td}>
                  {r.anulado ? (
                    <span style={{ color: '#B45309', fontSize: 10, fontWeight: 500, textTransform: 'uppercase' }}>Anulado</span>
                  ) : (
                    <span style={{ color: '#047857', fontSize: 10 }}>OK</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {total > PAGE_SIZE && (
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center', gap: 8, alignItems: 'center', fontSize: 12 }}>
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} style={btnGhost}>
            ← Anterior
          </button>
          <span>Página {page + 1} de {totalPaginas}</span>
          <button onClick={() => setPage(Math.min(totalPaginas - 1, page + 1))} disabled={page >= totalPaginas - 1} style={btnGhost}>
            Siguiente →
          </button>
        </div>
      )}
    </div>
  )
}

function FormaPagoPill({ forma }: { forma: string }) {
  const first = forma.split(',')[0].trim().toLowerCase()
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    efectivo: { bg: '#dcfce7', fg: '#166534', label: 'Efectivo' },
    deposito: { bg: '#dbeafe', fg: '#1e40af', label: 'Depósito' },
    cheque:   { bg: '#fef3c7', fg: '#92400e', label: 'Cheque' },
  }
  const s = map[first] ?? { bg: '#f4f4f5', fg: '#71717a', label: forma }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      background: s.bg, color: s.fg, fontSize: 10, fontWeight: 500,
    }}>{s.label}{forma.includes(',') ? ' +' : ''}</span>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6,
  fontSize: 12, background: 'var(--surface, #fff)', fontFamily: 'inherit', boxSizing: 'border-box',
}
const btnPrimary: React.CSSProperties = {
  padding: '8px 16px', border: 'none', borderRadius: 6,
  background: 'var(--brand-600, #FF5B04)', color: '#fff',
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
}
const btnGhost: React.CSSProperties = {
  padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6,
  background: 'var(--surface, #fff)', fontSize: 12, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
}
const th: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'left', fontSize: 11,
  color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500,
}
const td: React.CSSProperties = {
  padding: '8px 10px', verticalAlign: 'middle',
}
